import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { adminCarrierService, UpdateCarrierServiceData, CarrierServiceItem } from '../../services/admin.carrier.service';
import { errorService } from '../../services/error.service';
import useForm, { ValidationRules } from '../../hooks/useForm';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faPercent, faDollarSign, faUser, faBriefcase, faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';
import { formatPhoneNational, isValidPhone } from '../../config/phone.utils';
import styles from '../../styles/components/EditCarrierServiceModal.module.css';
import modalStyles from '../../styles/common/Modal.module.css';

interface EditCarrierServiceModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
    service: CarrierServiceItem | null;
}

const EditCarrierServiceModal: React.FC<EditCarrierServiceModalProps> = ({ show, onHide, onSuccess, service }) => {
    const [rateType, setRateType] = useState<'PERCENT' | 'FIX'>('PERCENT');

    const initialValues = useMemo<UpdateCarrierServiceData>(() => ({
        IdCarrierService: service?.id || 0,
        rate: service?.rate !== null && service?.rate !== undefined ? service.rate : undefined,
        isPercent: service?.isFix ? false : true,
        isFix: service?.isFix ? true : false,
        rateType: service?.isFix ? 'FIX' : 'PERCENT',
        contact: service?.contact || '',
        charge: service?.charge || '',
        email: service?.email || '',
        phoneNumber: service?.phoneNumber || ''
    }), [service]);

    const validationRules = useMemo<ValidationRules<UpdateCarrierServiceData>>(() => ({
        rate: (val) => {
            if (val !== undefined && val !== null && val < 0) return 'El cargo debe ser mayor o igual a 0';
            return null;
        },
        contact: (val) => {
            if (!val || val.trim() === '') return 'El contacto es requerido';
            if (val.length > 100) return 'El contacto no debe exceder los 100 caracteres';
            return null;
        },
        charge: (val) => {
            if (!val || val.trim() === '') return 'El cargo es requerido';
            if (val.length > 100) return 'El cargo no debe exceder los 100 caracteres';
            return null;
        },
        email: (val) => {
            if (!val || val.trim() === '') return 'El correo electrónico es requerido';
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(val)) return 'El correo electrónico no es válido';
            if (val.length > 100) return 'El correo no debe exceder los 100 caracteres';
            return null;
        },
        phoneNumber: (val) => {
            if (!val || val.trim() === '') return 'El teléfono es requerido';
            if (!isValidPhone(val, 'US')) return 'Número de teléfono inválido';
            if (val.length > 100) return 'El teléfono no debe exceder los 100 caracteres';
            return null;
        }
    }), []);

    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        setValues,
        setErrors,
        resetForm
    } = useForm<UpdateCarrierServiceData>(initialValues, validationRules);

    const [loading, setLoading] = useState(false);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const formatted = formatPhoneNational(val, 'US');
        setValues(prev => ({ ...prev, phoneNumber: formatted }));

        const isVal = isValidPhone(formatted, 'US');
        setErrors(prev => ({
            ...prev,
            phoneNumber: !formatted ? 'El teléfono es requerido' : !isVal ? 'Número de teléfono inválido' : undefined
        }));
    };

    // Update form when service prop changes
    useEffect(() => {
        if (show && service) {
            let phone = service.phoneNumber || '';
            if (phone.startsWith('+1')) {
                phone = phone.substring(2).trim();
            }
            const formattedPhone = formatPhoneNational(phone, 'US');
            const determinedRateType: 'PERCENT' | 'FIX' = service.isFix ? 'FIX' : 'PERCENT';
            setRateType(determinedRateType);

            setValues({
                IdCarrierService: service.id,
                rate: service.rate !== null && service.rate !== undefined ? service.rate : undefined,
                isPercent: determinedRateType === 'PERCENT',
                isFix: determinedRateType === 'FIX',
                rateType: determinedRateType,
                contact: service.contact || '',
                charge: service.charge || '',
                email: service.email || '',
                phoneNumber: formattedPhone
            });
        }
    }, [show, service, setValues]);

    const handleRateTypeChange = (newType: 'PERCENT' | 'FIX') => {
        setRateType(newType);
        setValues(prev => ({
            ...prev,
            rateType: newType,
            isPercent: newType === 'PERCENT',
            isFix: newType === 'FIX'
        }));
    };

    const onSubmit = async () => {
        try {
            setLoading(true);
            const payload: UpdateCarrierServiceData = { ...values };
            if (!service?.isMain) {
                delete payload.rate;
                delete payload.isPercent;
                delete payload.isFix;
                delete payload.rateType;
            } else {
                payload.isPercent = rateType === 'PERCENT';
                payload.isFix = rateType === 'FIX';
                payload.rateType = rateType;
            }
            if (payload.phoneNumber) {
                payload.phoneNumber = `+1 ${payload.phoneNumber}`;
            }
            const response = await adminCarrierService.updateCarrierService(payload);

            if (response.statusCode === 200) {
                onHide();
                onSuccess();
                await Swal.fire({
                    icon: 'success',
                    title: '¡Actualizado!',
                    text: 'El servicio ha sido actualizado correctamente.',
                    confirmButtonText: 'Aceptar',
                    confirmButtonColor: 'var(--elPaket-primary-color, #e84d30)'
                });
            } else {
                errorService.showErrorAlert(response, 'Error al Actualizar Servicio');
            }
        } catch (error: any) {
            console.error('Error in EditCarrierServiceModal:', error);
            errorService.showErrorAlert(error, 'Error de Conexión');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className={styles.editServiceModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    Editar Servicio de Courier
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className={styles.modalSubtitle}>
                    Ajusta los datos de contacto y el cargo sobre la tarifa negociada para este servicio.
                </p>
                <div className={styles.serviceBrief}>
                    <p className={styles.carrierName}>{service?.carrier}</p>
                    <h4 className={styles.serviceName}>{service?.name}</h4>
                </div>

                <Form onSubmit={handleSubmit(onSubmit)} id="edit-carrier-service-form" noValidate>
                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Contacto</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="contact"
                                    value={values.contact || ''}
                                    onChange={handleChange}
                                    placeholder="Nombre del contacto"
                                    maxLength={100}
                                    className={`${styles.updateUserModalInput} ${errors.contact ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.contact && <div className={styles.errorText}>{errors.contact}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Cargo Contacto</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faBriefcase} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="charge"
                                    value={values.charge || ''}
                                    onChange={handleChange}
                                    placeholder="Cargo del contacto"
                                    maxLength={100}
                                    className={`${styles.updateUserModalInput} ${errors.charge ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.charge && <div className={styles.errorText}>{errors.charge}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Correo Electrónico</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={values.email || ''}
                                    onChange={handleChange}
                                    placeholder="correo@ejemplo.com"
                                    maxLength={100}
                                    className={`${styles.updateUserModalInput} ${errors.email ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.email && <div className={styles.errorText}>{errors.email}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Teléfono</Form.Label>
                            <div className={`${styles.phoneInputWrapper} ${errors.phoneNumber ? styles.isInvalidWrapper : ''}`}>
                                <div className={styles.phonePrefix}>
                                    <img
                                        src="https://flagcdn.com/w40/us.png"
                                        srcSet="https://flagcdn.com/w80/us.png 2x"
                                        width="20"
                                        height="14"
                                        alt="US"
                                        className={styles.phonePrefixFlag}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <span className={styles.phonePrefixCode}>+1</span>
                                </div>
                                <Form.Control
                                    type="text"
                                    name="phoneNumber"
                                    value={values.phoneNumber || ''}
                                    onChange={handlePhoneChange}
                                    placeholder="Número telefónico"
                                    maxLength={100}
                                    disabled={loading}
                                />
                            </div>
                            {errors.phoneNumber && <div className={styles.errorText}>{errors.phoneNumber}</div>}
                        </Form.Group>

                        {service?.isMain && (
                            <div style={{ gridColumn: '1 / -1' }}>
                                <Form.Group>
                                    <Form.Label>Tipo de Cargo al Cliente</Form.Label>
                                    <div className={styles.rateTypeContainer}>
                                        <button
                                            type="button"
                                            className={`${styles.rateTypeBtn} ${rateType === 'PERCENT' ? styles.activeRateTypeBtn : ''}`}
                                            onClick={() => handleRateTypeChange('PERCENT')}
                                            disabled={loading}
                                        >
                                            <FontAwesomeIcon icon={faPercent} /> Porcentaje (%)
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.rateTypeBtn} ${rateType === 'FIX' ? styles.activeRateTypeBtn : ''}`}
                                            onClick={() => handleRateTypeChange('FIX')}
                                            disabled={loading}
                                        >
                                            <FontAwesomeIcon icon={faDollarSign} /> Monto Fijo ($ USD)
                                        </button>
                                    </div>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label>
                                        {rateType === 'PERCENT'
                                            ? 'Margen / Cargo Porcentual (%) sobre Tarifa Negociada'
                                            : 'Cargo Fijo Adicional ($ USD) sobre Tarifa Negociada'}
                                    </Form.Label>
                                    <div className={styles.inputWrapper}>
                                        <FontAwesomeIcon
                                            icon={rateType === 'PERCENT' ? faPercent : faDollarSign}
                                            className={styles.inputIcon}
                                        />
                                        <Form.Control
                                            type="number"
                                            name="rate"
                                            value={values.rate !== undefined && values.rate !== null ? values.rate : ''}
                                            onChange={(e) => {
                                                const { name, value } = e.target;
                                                setValues(prev => ({ ...prev, [name]: value === '' ? undefined : parseFloat(value) || 0 }));
                                            }}
                                            min="0"
                                            step="0.01"
                                            placeholder={rateType === 'PERCENT' ? 'Ej. 15 (%)' : 'Ej. 5.00 ($ USD)'}
                                            className={`${styles.updateUserModalInput} ${errors.rate ? styles.inputError : ''}`}
                                            disabled={loading}
                                        />
                                    </div>
                                    {errors.rate && <div className={styles.errorText}>{errors.rate}</div>}
                                </Form.Group>
                            </div>
                        )}
                    </div>
                </Form>
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                <Button 
                    variant="secondary" 
                    onClick={onHide} 
                    className={styles.secondaryBtn}
                    disabled={loading}
                >
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    type="submit" 
                    form="edit-carrier-service-form"
                    className={modalStyles.closeBtn}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Guardando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="me-2" /> Guardar Cambios
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditCarrierServiceModal;
